// =============================================================================
// MyActionsScreen — /my-actions  (T-350)
// One place to see everything on your plate across ALL projects: your open
// to-dos (project_user_todos, private to you) grouped by when they're due, plus
// upcoming reminders/tasks assigned to you (personal_bookings). No hunting job
// by job. RLS scopes both tables to the current user.
// =============================================================================

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ClipboardList, CheckCircle2, CalendarClock, AlertTriangle, Bell, FolderKanban, Plus, Banknote,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, KPI, Pill, Button, Field, Input, Select, Spinner, EmptyState } from "@/components/ui";
import { fmtDate, fmtMoney } from "@/lib/dates";

interface TodoRow {
  todo_id: string;
  project_id: string;
  body: string;
  due_date: string | null;
  projects: { project_code: string | null; project_name: string | null } | null;
}
interface BookingRow {
  booking_id: string;
  title: string;
  scheduled_start: string;
  scheduled_end: string | null;
  status: string;
  notes: string | null;
}

function daysTo(d: string | null): number | null {
  if (!d) return null;
  return Math.round((new Date(d + "T23:59:59").getTime() - Date.now()) / 86400000);
}
type Bucket = "overdue" | "today" | "week" | "later" | "nodate";
const BUCKET_META: Record<Bucket, { label: string; tone: any }> = {
  overdue: { label: "Overdue", tone: "critical" },
  today:   { label: "Due today", tone: "warning" },
  week:    { label: "This week", tone: "notice" },
  later:   { label: "Later", tone: "default" },
  nodate:  { label: "No due date", tone: "default" },
};
function bucketOf(due: string | null): Bucket {
  const d = daysTo(due);
  if (d == null) return "nodate";
  if (d < 0) return "overdue";
  if (d === 0) return "today";
  if (d <= 7) return "week";
  return "later";
}
function dueTone(due: string | null): string {
  const d = daysTo(due);
  if (d == null) return "text-text-muted";
  if (d <= 0) return "text-danger";
  if (d <= 2) return "text-warn";
  return "text-ok";
}

export default function MyActionsScreen() {
  const user = useRequireAuth();
  const qc = useQueryClient();
  const uid = user?.user_id;

  // All my open to-dos across every project.
  const { data: todos = [], isLoading: loadingTodos } = useQuery({
    queryKey: ["my_actions_todos", uid],
    enabled: !!uid,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_user_todos")
        .select("todo_id, project_id, body, due_date, projects:project_id (project_code, project_name)")
        .eq("done", false)
        .or(`owner_user_id.eq.${uid},created_by_user_id.eq.${uid}`)
        .order("due_date", { ascending: true, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as unknown as TodoRow[];
    },
  });

  // Upcoming reminders / tasks assigned to me (RLS scopes to me).
  const { data: bookings = [] } = useQuery({
    queryKey: ["my_actions_bookings", uid],
    enabled: !!uid,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("personal_bookings")
        .select("booking_id, title, scheduled_start, scheduled_end, status, notes")
        .in("status", ["scheduled", "in_progress"])
        .order("scheduled_start", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as BookingRow[];
    },
  });

  // T-362 — bills owing (manager+ only): a glance at what's entered in Xero and
  // awaiting payment. Full pay flow lives on /ap/bills.
  const isManagerPlus = hasRole(user, "manager");
  const { data: bills = [] } = useQuery({
    queryKey: ["my_actions_bills"],
    enabled: !!user && isManagerPlus,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ap_invoices")
        .select("ap_invoice_id, supplier_name, amount_inc_gst, due_date")
        .in("status", ["approved", "entered_in_xero"]).eq("xero_entry_type", "bill")
        .order("due_date", { ascending: true, nullsFirst: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });
  const billsSummary = useMemo(() => {
    const dTo = (d: string | null) => d == null ? null : Math.round((new Date(d + "T23:59:59").getTime() - Date.now()) / 86400000);
    const owing = bills.reduce((a, b) => a + Number(b.amount_inc_gst ?? 0), 0);
    const overdue = bills.filter(b => { const n = dTo(b.due_date); return n != null && n < 0; });
    return { count: bills.length, owing, overdue: overdue.length, next: bills[0] ?? null, dTo };
  }, [bills]);

  const grouped = useMemo(() => {
    const g: Record<Bucket, TodoRow[]> = { overdue: [], today: [], week: [], later: [], nodate: [] };
    for (const t of todos) g[bucketOf(t.due_date)].push(t);
    return g;
  }, [todos]);

  const counts = useMemo(() => ({
    open: todos.length,
    overdue: grouped.overdue.length,
    today: grouped.today.length,
    reminders: bookings.length,
  }), [todos, grouped, bookings]);

  async function complete(todoId: string) {
    await supabase.from("project_user_todos").update({ done: true }).eq("todo_id", todoId);
    qc.invalidateQueries({ queryKey: ["my_actions_todos", uid] });
  }

  const [adding, setAdding] = useState(false);

  if (!user) return null;

  return (
    <div className="p-4 md:p-6 max-w-[1000px] mx-auto pb-20">
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-[22px] md:text-2xl font-semibold tracking-tight flex items-center gap-2">
            <ClipboardList size={20} className="text-accent" /> My actions
          </h1>
          <p className="text-text-muted text-[13px] mt-1">
            Everything on your plate across all jobs — to-dos and reminders, in one place.
          </p>
        </div>
        <Button icon={Plus} onClick={() => setAdding(v => !v)}>{adding ? "Close" : "Add to-do"}</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <KPI label="Open to-dos" value={counts.open} icon={ClipboardList} />
        <KPI label="Overdue" value={counts.overdue} icon={AlertTriangle} accent={counts.overdue > 0} />
        <KPI label="Due today" value={counts.today} icon={CalendarClock} accent={counts.today > 0} />
        <KPI label="Reminders" value={counts.reminders} icon={Bell} sub="upcoming" />
      </div>

      {/* T-362 — bills owing glance (manager+) */}
      {isManagerPlus && billsSummary.count > 0 && (
        <Link to="/ap/bills" className="block mb-5">
          <Card className={billsSummary.overdue > 0 ? "border-danger/40" : ""}>
            <div className="p-4 flex items-center justify-between gap-3 hover:bg-ink-hover transition-colors rounded-lg">
              <div className="flex items-center gap-3 min-w-0">
                <Banknote size={18} className={billsSummary.overdue > 0 ? "text-danger" : "text-accent"} />
                <div className="min-w-0">
                  <div className="text-[14px] font-medium text-text">
                    {billsSummary.count} bill{billsSummary.count === 1 ? "" : "s"} to pay · {fmtMoney(billsSummary.owing)}
                  </div>
                  <div className="text-[11px] text-text-muted">
                    {billsSummary.overdue > 0
                      ? `${billsSummary.overdue} overdue`
                      : billsSummary.next?.due_date
                        ? `next due ${fmtDate(billsSummary.next.due_date)} · ${billsSummary.next.supplier_name ?? ""}`
                        : "entered in Xero, awaiting payment"}
                  </div>
                </div>
              </div>
              <span className="text-[12px] text-accent flex-shrink-0">Open →</span>
            </div>
          </Card>
        </Link>
      )}

      {adding && <AddTodoForm uid={uid!} onDone={() => { setAdding(false); qc.invalidateQueries({ queryKey: ["my_actions_todos", uid] }); }} />}

      {loadingTodos ? (
        <div className="p-12 flex justify-center"><Spinner size={20} /></div>
      ) : (
        <div className="space-y-5">
          {/* To-dos grouped by due */}
          {counts.open === 0 ? (
            <Card><EmptyState icon={CheckCircle2} title="Nothing on your list" description="No open to-dos across your jobs. Add one, or you're all clear." /></Card>
          ) : (
            (["overdue", "today", "week", "later", "nodate"] as Bucket[]).map(bucket => {
              const items = grouped[bucket];
              if (items.length === 0) return null;
              return (
                <div key={bucket}>
                  <div className="flex items-center gap-2 mb-2">
                    <Pill label={BUCKET_META[bucket].label} tone={BUCKET_META[bucket].tone} small />
                    <span className="text-[11px] text-text-muted">{items.length}</span>
                  </div>
                  <Card>
                    <div className="divide-y divide-line">
                      {items.map(t => (
                        <div key={t.todo_id} className="flex items-start gap-3 p-3">
                          <input type="checkbox" className="mt-0.5 flex-shrink-0" onChange={() => complete(t.todo_id)} title="Mark done" />
                          <div className="min-w-0 flex-1">
                            <div className="text-[13px] text-text">{t.body}</div>
                            <div className="text-[11px] text-text-muted mt-0.5 flex items-center gap-2 flex-wrap">
                              <Link to={`/projects/${t.project_id}`} className="inline-flex items-center gap-1 text-accent hover:underline">
                                <FolderKanban size={11} /> {t.projects?.project_code ?? "project"}
                              </Link>
                              {t.projects?.project_name && <span className="truncate">{t.projects.project_name}</span>}
                            </div>
                          </div>
                          {t.due_date && (
                            <span className={`font-mono text-[11px] flex-shrink-0 ${dueTone(t.due_date)}`}>{fmtDate(t.due_date)}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>
              );
            })
          )}

          {/* Reminders / personal bookings */}
          {bookings.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Bell size={13} className="text-text-muted" />
                <span className="text-[11px] uppercase tracking-wider text-text-muted">Reminders &amp; assigned tasks</span>
              </div>
              <Card>
                <div className="divide-y divide-line">
                  {bookings.map(b => (
                    <div key={b.booking_id} className="flex items-start gap-3 p-3">
                      <CalendarClock size={14} className="text-text-muted mt-0.5 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] text-text">{b.title}</div>
                        {b.notes && <div className="text-[11px] text-text-muted mt-0.5">{b.notes}</div>}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="font-mono text-[11px] text-text">{fmtDate(b.scheduled_start)}</div>
                        {b.status === "in_progress" && <Pill label="in progress" tone="pending" small />}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
function AddTodoForm({ uid, onDone }: { uid: string; onDone: () => void }) {
  const [projectId, setProjectId] = useState("");
  const [body, setBody] = useState("");
  const [due, setDue] = useState("");
  const [saving, setSaving] = useState(false);

  const { data: projects = [] } = useQuery({
    queryKey: ["my_actions_project_options"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("project_id, project_code, project_name, status")
        .not("status", "in", "(closed,cancelled,lost)")
        .order("project_code");
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  async function save() {
    if (!projectId || !body.trim()) return;
    setSaving(true);
    const { error } = await supabase.from("project_user_todos").insert({
      project_id: projectId, owner_user_id: uid, created_by_user_id: uid,
      body: body.trim(), due_date: due || null,
    });
    setSaving(false);
    if (!error) { setBody(""); setDue(""); onDone(); }
    else window.alert(`Couldn't add: ${error.message}`);
  }

  return (
    <Card className="mb-5">
      <div className="p-4 space-y-3">
        <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold">New to-do</div>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Project">
            <Select value={projectId} onChange={e => setProjectId(e.target.value)}>
              <option value="">— pick a project —</option>
              {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.project_code} · {p.project_name}</option>)}
            </Select>
          </Field>
          <Field label="Due (optional)">
            <Input type="date" value={due} onChange={e => setDue(e.target.value)} />
          </Field>
        </div>
        <Field label="What needs doing">
          <Input value={body} onChange={e => setBody(e.target.value)} placeholder="e.g. chase Ventia PO, book crane for Nyngan" />
        </Field>
        <div className="flex justify-end">
          <Button size="sm" disabled={!projectId || !body.trim() || saving} onClick={save}>
            {saving ? "Adding…" : "Add to-do"}
          </Button>
        </div>
      </div>
    </Card>
  );
}
